import { useState } from "react";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Workflow,
  Wand2,
  Send,
  Loader2,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Copy,
  PlaySquare,
} from "lucide-react";

const DEFAULT_PHONE = "+919588901928";
const STAGE_SCHEDULES = {
  0: [1, 2, 5, 10, 20],
  1: [1, 2, 4, 10, 20],
  2: [1, 2, 4, 6, 10, 20],
};
const SEQUENCE_DELAY_MS = 3000;

export function AdminWaFlowTriggerPanel({ onSent }) {
  const [open, setOpen] = useState(false);

  // Form fields
  const [userId, setUserId] = useState("");
  const [targetPhone, setTargetPhone] = useState(DEFAULT_PHONE);
  const [firstName, setFirstName] = useState("Amit");
  const [downloadStage, setDownloadStage] = useState(0);
  const [day, setDay] = useState(""); // "" = legacy int; "1","2",... = composite "N-Day D"
  const [force, setForce] = useState(true);

  // State
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [sequenceState, setSequenceState] = useState(null); // {current, total, results}

  const buildPayload = () => {
    const p = { force, campaign_id: "WASIMPLE_FLOW" };
    if (userId.trim()) p.user_id = userId.trim();
    if (targetPhone.trim()) p.target_phone = targetPhone.trim();
    if (firstName.trim()) p.first_name = firstName.trim();
    p.download_stage = Number(downloadStage);
    if (day !== "") p.day = Number(day);
    return p;
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreview(null);
    setResult(null);
    try {
      const res = await adminAPI.waFlowPreview(buildPayload());
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!userId.trim() && !targetPhone.trim()) {
      toast.error("Provide user_id or target_phone");
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const res = await adminAPI.waFlowTrigger(buildPayload());
      setResult(res.data);
      if (res.data.success) {
        toast.success("Flow triggered → WASimple");
        if (onSent) onSent();
      } else {
        toast.error(`Blocked: ${res.data.reason || "unknown"}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Trigger failed");
    } finally {
      setSending(false);
    }
  };

  const handleSendAllDaysForStage = async () => {
    if (sending || sequenceState) return;
    if (!userId.trim() && !targetPhone.trim()) {
      toast.error("Provide user_id or target_phone");
      return;
    }
    const stage = Number(downloadStage);
    const days = STAGE_SCHEDULES[stage] || [];
    if (days.length === 0) {
      toast.error("No days configured for this stage");
      return;
    }
    if (
      !window.confirm(
        `Fire ALL ${days.length} templates for stage ${stage} in sequence (${days.map((d) => `Day ${d}`).join(", ")}), ~${SEQUENCE_DELAY_MS / 1000}s apart? This sends real WhatsApp messages.`
      )
    )
      return;

    const results = [];
    setSequenceState({ current: 0, total: days.length, results });
    setResult(null);

    for (let i = 0; i < days.length; i++) {
      const d = days[i];
      setSequenceState({ current: i + 1, total: days.length, results: [...results] });
      try {
        const payload = { ...buildPayload(), day: d };
        const res = await adminAPI.waFlowTrigger(payload);
        const ok = res.data?.success;
        results.push({
          day: d,
          key: `${stage}-Day ${d}`,
          success: ok,
          status: res.data?.status_code,
          reason: res.data?.reason,
        });
      } catch (err) {
        results.push({
          day: d,
          key: `${stage}-Day ${d}`,
          success: false,
          status: err.response?.status,
          reason: err.response?.data?.detail || err.message,
        });
      }
      setSequenceState({ current: i + 1, total: days.length, results: [...results] });
      // Delay before next (except after last)
      if (i < days.length - 1) {
        await new Promise((r) => setTimeout(r, SEQUENCE_DELAY_MS));
      }
    }

    const ok = results.filter((r) => r.success).length;
    toast.success(`Sequence complete: ${ok}/${days.length} sent`);
    // Keep sequenceState so admin can see results; user can dismiss via new fire
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 overflow-hidden"
      data-testid="wa-flow-trigger-panel"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
        data-testid="wa-flow-toggle"
      >
        <div className="flex items-center gap-2">
          <Workflow className="h-4 w-4 text-indigo-600" />
          <span className="text-sm font-semibold text-[#18181B]">
            Trigger WASimple Flow
          </span>
          <span className="text-[11px] text-slate-500">
            POST per-user payload to WASimple — Flow picks the template
          </span>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        )}
      </button>

      {open && (
        <div className="p-4 border-t border-slate-200 space-y-3" data-testid="wa-flow-form">
          {/* Row 1: user_id OR ad-hoc phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldText
              label="user_id (preferred — real send)"
              value={userId}
              onChange={setUserId}
              placeholder="83330365-e0bd-4496-b8bd-a16449abc932"
              testid="wa-flow-userid"
            />
            <FieldText
              label="OR target_phone (ad-hoc preview)"
              value={targetPhone}
              onChange={setTargetPhone}
              placeholder="+919588901928"
              testid="wa-flow-phone"
            />
          </div>

          {/* Row 2: name + stage + force */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <FieldText
              label="first_name (override)"
              value={firstName}
              onChange={setFirstName}
              placeholder="Amit"
              testid="wa-flow-firstname"
            />
            <FieldSelect
              label="download_stage"
              value={String(downloadStage)}
              onChange={(v) => setDownloadStage(Number(v))}
              options={[
                { value: "0", label: "0 — library" },
                { value: "1", label: "1 — library" },
                { value: "2", label: "2 — upgrade popup" },
              ]}
              testid="wa-flow-stage"
            />
            <FieldSelect
              label="Simulate day (cron payload)"
              value={day}
              onChange={setDay}
              options={[
                { value: "", label: "— legacy (int stage)" },
                { value: "1", label: "Day 1" },
                { value: "2", label: "Day 2" },
                { value: "4", label: "Day 4" },
                { value: "5", label: "Day 5" },
                { value: "6", label: "Day 6" },
                { value: "10", label: "Day 10" },
                { value: "20", label: "Day 20" },
              ]}
              testid="wa-flow-day"
            />
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">
                Bypass 24h cooldown
              </label>
              <label className="flex items-center gap-2 h-9 px-2.5 rounded-md bg-white border border-slate-200">
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  className="h-4 w-4"
                  data-testid="wa-flow-force"
                />
                <span className="text-xs text-slate-700">force=true</span>
              </label>
            </div>
          </div>

          {/* Preview */}
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreview}
              disabled={previewLoading}
              data-testid="wa-flow-preview-btn"
            >
              {previewLoading ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Wand2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              Preview Payload
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSendAllDaysForStage}
              disabled={!!sequenceState || sending}
              data-testid="wa-flow-sequence-btn"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              {sequenceState && sequenceState.current < sequenceState.total ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending {sequenceState.current}/{sequenceState.total}…</>
              ) : (
                <><PlaySquare className="h-3.5 w-3.5 mr-1.5" /> Send All Days (stage {downloadStage})</>
              )}
            </Button>
            <span className="text-[11px] text-slate-500">
              Dry-run shows JSON; sequence fires every scheduled day ~3s apart.
            </span>
          </div>

          {/* Sequence progress + results */}
          {sequenceState && (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50/50 p-3"
              data-testid="wa-flow-sequence-result"
            >
              <p className="text-[11px] font-semibold text-amber-800 mb-1.5">
                Stage {downloadStage} sequence — {sequenceState.current}/{sequenceState.total}
              </p>
              <ul className="text-[11px] font-mono text-slate-700 space-y-0.5 max-h-56 overflow-auto">
                {(sequenceState.results || []).map((r, i) => (
                  <li key={i} className={r.success ? "text-emerald-700" : "text-red-600"}>
                    {r.success ? "✅" : "❌"} {r.key} — HTTP {r.status ?? "—"}{r.reason ? ` (${r.reason})` : ""}
                  </li>
                ))}
              </ul>
              {sequenceState.current >= sequenceState.total && (
                <div className="flex justify-end mt-2">
                  <Button size="sm" variant="ghost" onClick={() => setSequenceState(null)}>
                    Dismiss
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Preview block */}
          {preview && (
            <div
              className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2"
              data-testid="wa-flow-preview"
            >
              <PreviewRow label="webhook" value={preview.webhook_url} mono onCopy={copy} />
              <div>
                <label className="block text-[11px] font-medium text-slate-600 mb-1">
                  Sample payload
                </label>
                <pre className="font-mono text-[11px] bg-white border border-indigo-200 rounded px-2 py-1.5 overflow-auto">
                  {JSON.stringify(preview.sample_payload, null, 2)}
                </pre>
              </div>
              <p className="text-[11px] text-slate-600">
                Routing → <strong>{preview.cta_destination}</strong>. At send time
                <code className="font-mono bg-white px-1 mx-1 rounded">SAMPLE_TOKEN_…</code>
                is replaced with a unique 7-day signed token.
              </p>
              <div className="flex justify-end pt-1">
                <Button
                  onClick={handleSend}
                  disabled={sending || (!userId.trim() && !targetPhone.trim())}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  size="sm"
                  data-testid="wa-flow-send-btn"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5 mr-1.5" /> Fire Webhook
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div
              className={`rounded-lg border p-3 text-xs ${
                result.success
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-red-200 bg-red-50"
              }`}
              data-testid="wa-flow-result"
            >
              <div className="flex items-center gap-1.5 mb-2 font-semibold">
                {result.success ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span className="text-emerald-700">Webhook fired</span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-red-700">
                      Blocked / Failed{result.reason ? ` — ${result.reason}` : ""}
                    </span>
                  </>
                )}
                {result.status_code !== undefined && result.status_code !== null && (
                  <span className="text-slate-500 font-normal ml-2">
                    HTTP {result.status_code}
                  </span>
                )}
              </div>
              <pre className="font-mono text-[10px] whitespace-pre-wrap break-all text-slate-700 max-h-40 overflow-auto">
                {JSON.stringify(
                  {
                    payload: result.payload,
                    wasimple_response: result.response,
                    magic_link_full: result.magic_link_full,
                    token_id: result.token_id,
                    cta_destination: result.cta_destination,
                  },
                  null,
                  2
                )}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FieldText({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-600 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-9 px-2.5 rounded-md text-xs bg-white border border-slate-200 outline-none focus:border-indigo-500/50 font-mono"
        data-testid={testid}
      />
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[11px] font-medium text-slate-600 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-2 rounded-md text-xs bg-white border border-slate-200 outline-none focus:border-indigo-500/50"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function PreviewRow({ label, value, mono, onCopy }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[11px] font-medium text-slate-600 w-16 shrink-0 pt-0.5">
        {label}
      </span>
      <code
        className={`flex-1 px-2 py-1 rounded bg-white border border-indigo-200 text-[11px] break-all ${
          mono ? "font-mono" : ""
        }`}
      >
        {value}
      </code>
      <button
        onClick={() => onCopy(value, label)}
        className="shrink-0 p-1 rounded hover:bg-indigo-100 transition-colors"
        title="Copy"
      >
        <Copy className="h-3 w-3 text-slate-500" />
      </button>
    </div>
  );
}
